// Uwagi bota pod oceną i w zadaniach (patch-31b; od patch-31c w dwóch językach): ten sam tekst jest na ekranie i w nagraniu głosem ElevenLabs,
// a język (EN albo PL) wybiera przełącznik u góry ekranu lekcji („Język uwag bota”) — nigdy nie mieszamy obu naraz.
// Wypowiedź składa się z gotowych fraz (nagrania f.<klucz>) i nagrań pojedynczych słów (w.<słowo>) — np. „Powiedz całe zdanie — brakuje:” + is + water.
// Nic, co wymyśli rozpoznawanie mowy, nie trafia do głośnika: słowo bez nagrania jest pomijane w dźwięku (na ekranie zostaje).
// Czysta logika bez DOM i sieci.
import { audioIds } from '../speech/audioIds.ts';
import type { Verdict } from '../types.ts';
import { spokenForm } from './pronunciation.ts';
import { tokenize } from './text.ts';

/** Frazy bota (po polsku). Klucz = część nazwy nagrania f.<klucz>. Zmiana tekstu = nowe nagranie (npm run audio:generate). */
export const FEEDBACK = {
  ok: 'Brawo!',
  petarda: 'Petarda!', // pochwały za bardzo dobry wynik (3★) — losowane obok „Brawo!” (pickPraise)
  idealnie: 'Idealnie!',
  maszTo: 'Masz to!',
  sztos: 'Sztos!',
  ogien: 'Ogień!',
  good: 'Dobrze!', // „Uzupełnij” i „Ułóż zdanie”: napisu nie ma (wynik pokazuje kolor pola), „Dobrze!” mówi bot
  notYet: 'Jeszcze nie to. Spróbuj zmienić kolejność.', // „Ułóż zdanie”, błędny układ: mówi bot, napisu nie ma
  notYetFix: 'Jeszcze nie to. Spróbuj jeszcze raz albo kliknij „Popraw”.', // po kilku nieudanych próbach (config.session.fixAfterAttempts) pojawia się przycisk „Popraw”
  almostLetters: 'Prawie! Sprawdź literki.', // „Uzupełnij wyrażenie”: jedna literówka
  tryAgain: 'Spróbuj jeszcze raz.',
  practice: 'Brawo! Jeszcze potrenuj:',
  all: 'Powiedz całe zdanie — brakuje:',
  allAlmost: 'Prawie! Powiedz całe zdanie — brakuje:',
  allSome: 'Powiedz całe zdanie — brakuje kilku słów.',
  startLate: 'Zacznij mówić dopiero, gdy przycisk zacznie pulsować.',
  slower: 'Mów wolniej i wyraźniej.',
  workOn: 'Popracuj nad:',
  unclear: 'Prawie! Nie jestem pewien, czy usłyszałem całe słowa. Powiedz jeszcze raz wyraźnie — także końcówki słów.',
  heard: 'Usłyszałem:',
  heardMaybe: 'Wydaje mi się, że usłyszałem:',
  silent: 'Nic nie słyszę. Powiedz trochę głośniej i spróbuj jeszcze raz.',
  silentMore: 'Dalej nic nie słyszę. Spróbuj jeszcze raz, bliżej mikrofonu.',
  micOff: 'Nie mogę włączyć mikrofonu. Poproś rodzica o pomoc.',
  network: 'Nie udało się sprawdzić, jak mówisz — połączenie zawiodło. Spróbuj jeszcze raz.',
  noCheck: 'Automatyczna ocena jest teraz niedostępna. Wybierz, jak ci poszło.',
  typeWord: 'Wpisz słowo 🙂',
} as const;

/** Te same frazy po angielsku (tryb „uwagi bota po angielsku”). Zmiana tekstu = nowe nagranie (npm run audio:generate). */
export const FEEDBACK_EN: Record<keyof typeof FEEDBACK, string> = {
  ok: 'Well done!',
  petarda: 'Awesome!',
  idealnie: 'Perfect!',
  maszTo: 'You got it!',
  sztos: 'Epic!',
  ogien: 'On fire!',
  good: 'Good!',
  notYet: 'Not quite. Try changing the order.',
  notYetFix: 'Not quite. Try again, or tap the new button to see the answer.',
  almostLetters: 'Almost! Check the letters.',
  tryAgain: 'Try again.',
  practice: 'Well done! Practice a bit more:',
  all: 'Say the whole sentence — missing:',
  allAlmost: 'Almost! Say the whole sentence — missing:',
  allSome: 'Say the whole sentence — a few words are missing.',
  startLate: 'Start speaking only when the button starts to pulse.',
  slower: 'Speak slower and more clearly.',
  workOn: 'Work on:',
  unclear: 'Almost! I am not sure I heard all the words. Say it again clearly — including the word endings.',
  heard: 'I heard:',
  heardMaybe: 'I think I heard:',
  silent: 'I can not hear anything. Speak a little louder and try again.',
  silentMore: 'I still can not hear anything. Try again, closer to the microphone.',
  micOff: 'I can not turn on the microphone. Ask a parent for help.',
  network: 'I could not check how you speak — the connection failed. Try again.',
  noCheck: 'Automatic checking is not available now. Choose how it went.',
  typeWord: 'Type a word 🙂',
};

export type FeedbackKey = keyof typeof FEEDBACK;

/** Język uwag bota (to samo, co `TeacherLang`). */
export type FeedbackLang = 'en' | 'pl';

/** Tekst frazy w wybranym języku. */
export function feedbackText(key: FeedbackKey, lang: FeedbackLang): string {
  return lang === 'en' ? FEEDBACK_EN[key] : FEEDBACK[key];
}

/** Podpowiedź w „Uzupełnij wyrażenie”: pierwsza litera i liczba liter (tekst na ekranie, nie nagranie). */
export function hintText(letter: string, length: number, lang: FeedbackLang): string {
  return lang === 'en' ? `Hint: it starts with “${letter}” and has ${length} letters.` : `Podpowiedź: zaczyna się na „${letter}” i ma ${length} liter.`;
}

/** Pojedyncza fraza bota do odtworzenia: nagranie f.<klucz> (po polsku) albo f.<klucz>.en (po angielsku), a bez pliku — głos zapasowy z tym samym tekstem (dla `player.playAll`). */
export function sayKey(key: FeedbackKey, lang: FeedbackLang = 'pl'): { id: string; fallback: { text: string; lang: FeedbackLang } } {
  return { id: audioIds.feedback(key, lang), fallback: { text: feedbackText(key, lang), lang } };
}

/** Pochwały za bardzo dobry wynik (3★, bez słabych słów); „Brawo!” (`ok`) zostaje jedną z nich, a przy zwykłym zaliczeniu (2★) jest jedyną. */
export const PRAISE: readonly FeedbackKey[] = ['ok', 'petarda', 'idealnie', 'maszTo', 'sztos', 'ogien'];

/**
 * Która pochwała pasuje do wyniku. Bardzo dobry wynik (zaliczone, 3★, żadnego słabego słowa) → losowa z `PRAISE`, ale nie ta sama co poprzednio
 * (`last`), żeby bot nie powtarzał się dwa razy z rzędu. Cała reszta → „Brawo!”. `rnd` wstrzykujemy w testach.
 */
export function pickPraise(v: Pick<Verdict, 'passed' | 'stars' | 'reason' | 'weakWords'>, last?: FeedbackKey, rnd: () => number = Math.random): FeedbackKey {
  if (!(v.passed && v.reason === 'ok' && v.stars >= 3 && v.weakWords.length === 0)) return 'ok';
  const pool = PRAISE.filter((k) => k !== last);
  return pool[Math.min(pool.length - 1, Math.floor(rnd() * pool.length))] ?? 'ok';
}

/** Jeden klocek: fraza z nagraniem albo lista słów angielskich (każde z własnym nagraniem). */
export type Part = { key: FeedbackKey } | { words: readonly string[] };

/** Klocek do odtworzenia: nagranie `id`, a gdy go brak — tekst do przeczytania głosem zapasowym. */
export interface FeedbackClip {
  id: string;
  text: string;
  lang: FeedbackLang;
}

/** Z czego składa się komunikat o wyniku próby (to, co dawniej zwracało samo `reasonPl`). */
export function reasonParts(v: Verdict, praise: FeedbackKey = 'ok'): Part[] {
  switch (v.reason) {
    case 'ok':
      return v.weakWords.length > 0 ? [{ key: 'practice' }, { words: v.weakWords }] : [{ key: praise }];
    case 'incomplete':
      if (v.missing.length === 0) return [{ key: 'allSome' }];
      // pierwsze słowo ginie najczęściej dlatego, że dziecko zaczęło mówić, zanim mikrofon był gotowy
      return [{ key: v.stars >= 2 ? 'allAlmost' : 'all' }, { words: v.missing }, ...(v.missingFirst ? [{ key: 'startLate' as const }] : [])];
    case 'low-accuracy':
      return [{ key: 'slower' }];
    case 'weak-words':
      return [{ key: 'workOn' }, { words: v.weakWords }];
    case 'unclear':
      return [{ key: 'unclear' }];
  }
}

/** Tekst na ekranie: frazy rozdzielone spacją, lista słów po przecinku i z kropką („… brakuje: is, water.”). */
export function partsText(parts: readonly Part[], lang: FeedbackLang = 'pl'): string {
  return parts.map((p) => ('key' in p ? feedbackText(p.key, lang) : `${p.words.join(', ')}.`)).join(' ');
}

/** Nagrania do odtworzenia po kolei. Słowo bez nagrania (`hasClip` = false) jest pomijane. */
export function partsClips(parts: readonly Part[], hasClip: (word: string) => boolean, lang: FeedbackLang = 'pl'): FeedbackClip[] {
  const clips: FeedbackClip[] = [];
  for (const p of parts) {
    if ('key' in p) clips.push({ id: audioIds.feedback(p.key, lang), text: feedbackText(p.key, lang), lang });
    else for (const w of p.words) if (hasClip(w)) clips.push({ id: audioIds.word(w), text: spokenForm(w), lang: 'en' });
  }
  return clips;
}

/** Co usłyszało rozpoznawanie: „Usłyszałem: „This.”” (przy niepewnym wyniku „Wydaje mi się, że usłyszałem: …”). */
export function heardLine(label: 'heard' | 'heardMaybe', pretty: string, lang: FeedbackLang = 'pl'): string {
  return lang === 'en' ? `${FEEDBACK_EN[label]} “${pretty}”` : `${FEEDBACK[label]} „${pretty}”`;
}

/**
 * Nagrania dla linijki „Usłyszałem …”: fraza i słowa po angielsku. Czytamy ją tylko wtedy, gdy KAŻDE usłyszane słowo ma nagranie — inaczej
 * bot powiedziałby urwane zdanie, a słowo, którego nie ma w słowniku lekcji, nie powinno trafić do głośnika.
 */
export function heardClips(label: 'heard' | 'heardMaybe', recognized: string, hasClip: (word: string) => boolean, lang: FeedbackLang = 'pl'): FeedbackClip[] {
  const words = tokenize(recognized);
  if (words.length === 0 || !words.every((w) => hasClip(w))) return [];
  return partsClips([{ key: label }, { words }], hasClip, lang);
}

/** Pomocniczo dla planu nagrań: wszystkie frazy z kluczami. */
export function feedbackPhrases(): { key: FeedbackKey; lang: FeedbackLang; text: string }[] {
  return (Object.keys(FEEDBACK) as FeedbackKey[]).flatMap((key) => (['pl', 'en'] as const).map((lang) => ({ key, lang, text: feedbackText(key, lang) })));
}

